import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from '@rails/activestorage'

import locales from '../uploader-translations/locales.json'

// Connects to data-controller="uploader"
export default class extends Controller {

  uploader = this.element
  fileInput = this.element.querySelector('input[type=file]')
  uploadedBlobField = this.element.querySelector('#blob-keys')
  saveTempBtn = this.element.querySelector('#save')

  connect(){
    const controller = this
    // remove for present items
    this.uploader.querySelectorAll('.uploader-preview-item').forEach((el) => controller.addRemoveItemCallback(el))
    // drag drop
    this.attachDragListeners()
    // file input
    this.fileInput.addEventListener('change', function(e){
      controller.onFileAdded(e.target)
    })
    // set auto-process queue
    this.autoProcessQueue = this.element.dataset.autoprocess !== undefined
  }

  addRemoveItemCallback(item){
    const controller = this

    item.querySelector('.delete-btn').addEventListener('click', function(e){
      const targetItem = this.parentElement.parentElement
      const uploadedBlobKeys = JSON.parse(controller.uploadedBlobField.value)
      const blobKey = targetItem.dataset.key

      const filteredKeys = uploadedBlobKeys.filter((k) => k != blobKey) // allows for string ~ int comparison
      controller.uploadedBlobField.value = JSON.stringify(filteredKeys)

      targetItem.remove()

      const hasTempItems = controller.uploader.querySelector('.uploader-preview-item')

      if(filteredKeys.length == 0) delete controller.uploader.querySelector('#preview').dataset.present
      if(!hasTempItems && controller.saveTempBtn) controller.saveTempBtn.classList = 'hidden'
    })
  }

  attachDragListeners(){
    const controller = this
    const dragEl = this.uploader.querySelector('.uploader')

    let dragWatcher = 0 // https://stackoverflow.com/a/21002544

    // start dragging
    dragEl.addEventListener('dragenter', function(e){
      e.preventDefault()
      e.stopPropagation()

      dragWatcher++

      this.classList.add('dragging')
    })
      // when its in the area
    dragEl.addEventListener('dragover', function(e){
      e.preventDefault()
      e.stopPropagation()

      this.classList.remove('border-input')
      this.classList.add('border-primary', 'bg-primary/5')
    })
    // when it leaves the area
    dragEl.addEventListener('dragleave', function(e){
      e.preventDefault()
      e.stopPropagation()

      dragWatcher--
      if(dragWatcher === 0) this.classList.remove('dragging')

      this.classList.remove('border-primary', 'bg-primary/5')
      this.classList.add('border-input')
    })
    // when dropped inside
    dragEl.addEventListener('drop', function(e){
      e.preventDefault()
      e.stopPropagation()

      this.classList.remove('border-primary', 'bg-primary/5', 'dragging')
      this.classList.add('border-input')

      controller.onFileAdded(e.dataTransfer)
    })
  }

  onFileAdded(inputObj){
    if (inputObj.files.length === 0) return

    const allowedTypes = this.fileInput.accept.split(',').map(item => item.trim())
    const filesAllowed = Array.from(inputObj.files).every((f) => 
      allowedTypes.some(type => {
        if (type == '*') return true

        if (type.endsWith('/*')) {
          // Accept wildcard MIME type (e.g., video/*)
          const baseType = type.split('/')[0];
          return f.type.startsWith(baseType)
        } else if (type.startsWith('.')) {
          // Accept file extension
          return f.name.toLowerCase().endsWith(type.toLowerCase())
        } else {
          // Accept specific MIME type
          return f.type === type
        }
      })
    )
    if(!filesAllowed){
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'alert', message: locales['it'].invalid_file } }))
      return
    }

    if (this.fileInput.multiple) {
      this.uploadMultiple(this.fileInput.name, inputObj.files)
    } else {
      this.upload(this.fileInput.name, inputObj.files[0])
    }
  }

  upload(name, file) {
    this.startUpload({
      name: name,
      files: [file],
      multiple: false
    })
  }

  uploadMultiple(name, files) {
    this.startUpload({
      name: name,
      files: Array.from(files),
      multiple: true
    })
  }

  startUpload(uploadObj){
    if(this.autoProcessQueue) {
      this.processQueue(uploadObj)
    }else {
      this.addToQueue(uploadObj)
    }
  }

  addToQueue(uploadObj){
    // direct-uploads blobs and adds it to the "preview" queue
    const isMultiple = uploadObj.multiple;

    const controller = this
    const previewDiv = this.element.querySelector('#preview')
    uploadObj.files.forEach((file) => {
      const upload = new DirectUpload(
        file,
        '/rails/active_storage/direct_uploads', // This url is exposed by default in your app
      );
      upload.create((error, blob) => {
        if(error){
          console.log("ERROR UPLOADING FILE:", error)
        }else{
          console.log(blob)
          // controller.uploader.querySelector('#placeholder').classList.add('hidden')

          let uploadedBlobKeys = JSON.parse(controller.uploadedBlobField.value)

          if(!isMultiple && uploadedBlobKeys.length){
            // remove previous preview
            controller.uploader.querySelectorAll('.uploader-preview-item').forEach((el) => el.remove())
            // empty blob keys
            uploadedBlobKeys = []
          }

          const tpl = controller.uploader.querySelector("#blob-preview-tpl")
          const previewItem = tpl.content.firstElementChild.cloneNode(true)
          if(blob.thumb_url){
            previewItem.querySelector('#thumb').style.backgroundImage = `url(${blob.thumb_url})`
          }else{
            previewItem.querySelector('#thumb').innerHTML = `<i class="!text-2xl ${blob.file_icon}"></i>`
          }
          
          previewItem.dataset.key = blob.key
          // add filename if not image
          // if(!blob.content_type.match('image')){
            const filenameDiv = previewItem.querySelector('#filename')
            filenameDiv.textContent = blob.filename
            const filesizeDiv = previewItem.querySelector('#filesize')
            filesizeDiv.textContent = blob.filesize
            const customFields = previewItem.querySelector('#custom_fields')
            if(customFields) customFields.querySelectorAll('input').forEach((el) => el.setAttribute('name', `custom_fields[${blob.key}][${el.name}]`))
          // }
          controller.addRemoveItemCallback(previewItem)

          // add to current keys
          uploadedBlobKeys.push(blob.key)
          controller.uploadedBlobField.value = JSON.stringify(uploadedBlobKeys)

          previewDiv.prepend(previewItem)

          controller.uploader.querySelector('#preview').dataset.present = ''
          if(controller.saveTempBtn) controller.saveTempBtn.classList = 'block'
        }
      });
    });
  }

  processQueue(uploadObj){
    // submits a form to the "data-uploader-url" endpoint
    const isMultiple = uploadObj.multiple

    const uploadUrl = this.element.dataset.uploaderUrl
    const formData = new FormData()
    if(isMultiple){
      uploadObj.files.forEach((file) => formData.append(`${uploadObj.name}[]`, file))
    }else{
      formData.append(uploadObj.name, uploadObj.files[0])
    }

    window.dispatchEvent(new CustomEvent('upload-start'))

    axios({
      method: 'POST',
      url: uploadUrl,
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      },
      data: formData
    })
    .then((res) => {
      console.log(res)
      window.dispatchEvent(new CustomEvent('upload-end'))
    })
    .catch((err) => {
      console.log(err)
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'alert', message: locales['it'].error } }))
    })
  }
}